import {Fragment} from 'react';
import {FlatList, ListRenderItemInfo, StyleSheet, View} from 'react-native';
import AVText from 'src/components/elements/AVText';
import CustomSearchbar from 'src/components/elements/CustomSearchField';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import SearchIcon from 'src/components/img/svg/SearchIcon';
import ProductCategories from 'src/components/screens/scanAndPay/ProductCategories';
import SearchProductItem from 'src/components/screens/scanAndPay/SearchProductItem';
import {scanAndPayTemplate} from 'src/components/screens/scanAndPay/templates/scanandPayThemeHelper';
import Styles from 'src/components/Styles';
import Localized from 'src/constants/AppStrings';
import {ProductCategory} from 'src/types/ProductCategory';
import {ProductType} from 'src/api/CartApi';

const {subHeaderFontSize, mainFontFamily} = getLayoutDescriber();

export function searchProductsScreenDescriptor(props: {
  products: ProductType[];
  query: string;
  categories: ProductCategory[];
  selectedCategoryKey: string | number;
  fetchProducts: () => Promise<void>;
  itemSelected: (item: ProductType) => void;
  onSearchChanged: (search: string) => void;
  onCategoryPress: (category: ProductCategory) => void;
  fetching: boolean;
}): JSX.Element {
  const {
    products,
    query,
    categories,
    selectedCategoryKey,
    fetchProducts,
    onSearchChanged,
    onCategoryPress,
    fetching,
    itemSelected,
  } = props;

  const renderProduct = ({item, index}: ListRenderItemInfo<ProductType>) => {
    const isLast = index === products.length - 1;
    return (
      <>
        <SearchProductItem
          onPress={() => itemSelected(item)}
          price={item.price}
          name={item.name}
          isProductCat={true}
          rewardpoints={item.rewardpoints}
        />
        {scanAndPayTemplate.showDividerScanPay && !isLast && (
          <View style={styles.dividerStyle} />
        )}
      </>
    );
  };
  return (
    <>
      <CustomSearchbar
        onChangeText={onSearchChanged}
        value={query}
        placeholder={Localized.Labels.searchproducts}
        containerStyle={styles.searchWrapper}
        removeShadow={true}
        searchTextStyle={styles.searchInput}
        searchIcon={<SearchIcon />}
      />
      <ProductCategories
        categories={categories}
        selectedCategory={selectedCategoryKey}
        onCategoryPress={onCategoryPress}
      />

      <FlatList
        style={styles.productsList}
        data={products}
        onEndReachedThreshold={0.5}
        onEndReached={() => fetchProducts()}
        renderItem={(props) => renderProduct(props)}
        keyExtractor={(product) => product.id}
        ListFooterComponent={<View style={styles.footerView} />}
        ListEmptyComponent={
          (fetching || products.length === 0) &&
          query.length > 0 && (
            <View style={styles.emptyContainer}>
              <AVText accessibilityRole="text" style={styles.emptyContent}>
                {Localized.Labels.no_products_available}
              </AVText>
            </View>
          )
        }
      />
    </>
  );
}

const styles = StyleSheet.create({
  emptyList: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
    marginHorizontal: Styles.Spacing.m3,
  },
  emptyListText: {
    fontSize: subHeaderFontSize(),
    fontWeight: '400',
    fontFamily: mainFontFamily(),
    color: Styles.Colors.PayNew.black01,
    textAlign: 'center',
  },
  footerView: {
    height: 50,
  },
  productsList: {
    paddingTop: Styles.Spacing.m2,
  },
  searchInput: {
    fontSize: subHeaderFontSize(),
    fontWeight: '400',
    fontFamily: mainFontFamily(),
    color: Styles.Colors.PayNew.darkHuesBase04,
  },
  searchWrapper: {
    backgroundColor: Styles.white,
    marginHorizontal: Styles.Spacing.m3,
    marginTop: Styles.Spacing.m3,
    borderRadius: Styles.Spacing.m4,
  },
  emptyContent: {
    fontSize: subHeaderFontSize(),
    fontWeight: '400',
    fontFamily: mainFontFamily(),
    textAlign: 'center',
    color: '#000',
  },
  emptyContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: Styles.Spacing.m3,
    marginHorizontal: Styles.Spacing.m3,
  },
  dividerStyle: {
    borderBottomColor: 'rgba(178, 178, 178, 0.70)',
    borderBottomWidth: 1,
    marginHorizontal: Styles.Spacing.m3,
    marginVertical: Styles.Spacing.m2 + 2,
  },
});

export default {
  searchProductsScreenDescriptor,
};
